import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { emitCopilotMutation } from "../mutations";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const updateProductSkill: Skill = {
  id: "updateProduct",
  description:
    "Actualiza propiedades de un producto existente. Úsala para CAMBIAR cualquier campo: nombre, precio, descripción, tipo, o moverlo a otra categoría (categoryId). Solo envía los campos que quieres modificar. Si necesitas el productId o categoryId, usa searchProduct / listCategories primero — NUNCA pidas IDs al usuario directamente.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      productId: { type: "string", description: "ID del producto a actualizar" },
      name: { type: "string", description: "Nuevo nombre (opcional)" },
      price: { type: "number", description: "Nuevo precio (opcional, >= 0)" },
      description: {
        type: "string",
        description: "Nueva descripción (opcional)",
      },
      categoryId: {
        type: "string",
        description:
          "Nueva categoría (opcional). Para moverlo a otra categoría — obtén el ID con listCategories.",
      },
      type: {
        type: "string",
        enum: ["prepared", "on_demand"],
        description:
          "Legacy. Cambiar `type` directamente solo si es un producto individual. " +
          "Para convertir entre individual y combo no uses esta skill; un combo se " +
          "crea con createProduct(productKind='combo') y sus componentes se editan con setComboItems.",
      },
      preparationType: {
        type: "string",
        enum: ["prepared", "on_demand"],
        description:
          "Cambiar modo de preparación (opcional). Aplica también a combos. " +
          "'prepared' = listo, no pasa por cocina. 'on_demand' = requiere cocina.",
      },
    },
    required: ["restaurantId", "productId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) {
      return { ok: false, error: "Sesión no disponible." };
    }
    const restaurantId = String(args.restaurantId ?? "");
    const productId = String(args.productId ?? "");
    if (!restaurantId || !productId) {
      return { ok: false, error: "Faltan restaurantId o productId." };
    }

    const patch: Record<string, unknown> = {};
    const details: Array<{ label: string; value: string }> = [];

    if (typeof args.name === "string" && args.name.trim()) {
      patch.name = String(args.name).trim();
      details.push({ label: "Nuevo nombre", value: patch.name as string });
    }
    if (Number.isFinite(Number(args.price)) && Number(args.price) >= 0) {
      patch.price = Number(args.price);
      details.push({
        label: "Nuevo precio",
        value: `$${(patch.price as number).toLocaleString()}`,
      });
    }
    if (typeof args.description === "string") {
      patch.description = String(args.description);
      details.push({ label: "Descripción", value: patch.description as string });
    }
    if (typeof args.categoryId === "string" && args.categoryId) {
      patch.categoryId = String(args.categoryId);
      details.push({ label: "Mover a categoría", value: "(otra)" });
    }
    if (args.type === "prepared" || args.type === "on_demand") {
      patch.type = args.type;
      details.push({
        label: "Tipo (legacy)",
        value: args.type === "on_demand" ? "Bajo pedido" : "Preparado",
      });
    }
    if (
      args.preparationType === "prepared" ||
      args.preparationType === "on_demand"
    ) {
      patch.preparationType = args.preparationType;
      details.push({
        label: "Modo",
        value:
          args.preparationType === "on_demand"
            ? "Bajo pedido (pasa por cocina)"
            : "Preparado (no pasa por cocina)",
      });
    }

    if (Object.keys(patch).length === 0) {
      return {
        ok: false,
        error: "No se especificó ningún campo para actualizar.",
      };
    }

    const ok = await ctx.confirmation.confirm({
      title: "Actualizar producto",
      description: "Vas a modificar este producto en el catálogo.",
      details,
      confirmLabel: "Actualizar",
    });
    if (!ok) {
      return { ok: false, error: "Acción cancelada por el usuario." };
    }

    if (ctx.follow.enabled) {
      await ctx.follow.step("Actualizando producto…", { durationMs: 600 });
      await ctx.follow.clickAt(`[data-copilot-tab="productos"]`, {
        durationMs: 700,
        label: "Productos",
      });
      ctx.router.push(
        `/dashboard/restaurantes/${restaurantId}?tab=productos`,
      );
      await sleep(900);
      await ctx.follow.step("Guardando cambios…", { durationMs: 500 });
    }

    try {
      const { data } = await apiWithAuth.patch(
        `/restaurants/${restaurantId}/products/${productId}`,
        patch,
        ctx.accessToken,
      );

      if (ctx.follow.enabled) {
        await ctx.follow.step("✓ Producto actualizado", {
          durationMs: 900,
          spinner: false,
        });
        ctx.follow.clear();
      }

      emitCopilotMutation({
        scope: "products",
        action: "updated",
        restaurantId,
        id: productId,
      });

      return {
        ok: true,
        result: {
          id: data?.id ?? productId,
          name: data?.name,
          price: data?.price,
          categoryId: data?.categoryId,
          updated: Object.keys(patch),
        },
        humanSummary: `Producto actualizado (${Object.keys(patch).join(", ")}).`,
      };
    } catch (err: any) {
      if (ctx.follow.enabled) {
        await ctx.follow.step("✗ Error al actualizar", {
          durationMs: 700,
          spinner: false,
        });
        ctx.follow.clear();
      }
      return {
        ok: false,
        error:
          err?.response?.data?.message ||
          err?.message ||
          "Error actualizando producto.",
      };
    }
  },
};
